import { useState, useLayoutEffect, type CSSProperties, type RefObject } from 'react';

/**
 * Clamp a popup position to stay within viewport bounds.
 * Use for context menus, color pickers, and any fixed-position popup.
 */
export function clampToViewport(
  x: number,
  y: number,
  popupWidth: number,
  popupHeight: number,
  margin = 8,
): { left: number; top: number } {
  let left = x;
  let top = y;
  if (left + popupWidth > window.innerWidth - margin) left = Math.max(margin, window.innerWidth - popupWidth - margin);
  if (top + popupHeight > window.innerHeight - margin) top = Math.max(margin, window.innerHeight - popupHeight - margin);
  return { left, top };
}

interface DropdownPositionOptions {
  offset?: number;
  minWidth?: number;
  matchTriggerWidth?: boolean;
}

/**
* Hook per calcolare la posizione di un dropdown rispetto al suo trigger element.
* Gestisce automaticamente il posizionamento, lo scroll e il resize della finestra.
* 
* @param isOpen - Se true, calcola la posizione; se false, restituisce null
* @param triggerRef - Ref all'elemento trigger del dropdown
* @param options - Opzioni di configurazione per il posizionamento
* @param options.offset - Offset verticale in pixel dal trigger (default: 6)
* @param options.minWidth - Larghezza minima del dropdown in pixel
* @param options.matchTriggerWidth - Se true, il dropdown avrà la stessa larghezza del trigger
* @returns Stili CSS per posizionare il dropdown, o null se isOpen è false
*/
export function useDropdownPosition(
  isOpen: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  { offset = 6, minWidth, matchTriggerWidth = false }: DropdownPositionOptions = {}
) {
  const [position, setPosition] = useState<CSSProperties | null>(null);

  useLayoutEffect(() => {
  if (!isOpen || !triggerRef.current) {
      setPosition(null);
      return;
  }

  const updatePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();

      const spaceBelow = window.innerHeight - rect.bottom - offset;
      const spaceAbove = rect.top - offset;
      const minDropdownHeight = 260;
      const openAbove = spaceBelow < minDropdownHeight && spaceAbove > spaceBelow;

      // Horizontal overflow: clamp left so dropdown doesn't exceed right edge
      const dropdownWidth = matchTriggerWidth ? rect.width : (minWidth || 200);
      let left = rect.left;
      if (left + dropdownWidth > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - dropdownWidth - 8);
      }

      const style: CSSProperties = openAbove
    ? {
        position: 'fixed',
        bottom: `${window.innerHeight - rect.top + offset}px`,
        left: `${left}px`,
        zIndex: 9999,
        ...(matchTriggerWidth && { width: `${rect.width}px` }),
        ...(minWidth && !matchTriggerWidth && { minWidth: `${minWidth}px` }),
        maxHeight: `${spaceAbove - 8}px`,
      }
    : {
        position: 'fixed',
        top: `${rect.bottom + offset}px`,
        left: `${left}px`,
        zIndex: 9999,
        ...(matchTriggerWidth && { width: `${rect.width}px` }),
        ...(minWidth && !matchTriggerWidth && { minWidth: `${minWidth}px` }),
        maxHeight: `${spaceBelow - 8}px`,
      };

      setPosition(style);
  };

  updatePosition();
  window.addEventListener('scroll', updatePosition, true);
  window.addEventListener('resize', updatePosition);

  return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
  };
  }, [isOpen, offset, minWidth, matchTriggerWidth, triggerRef]);

  return position;
}

